import React, { useMemo, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Loader from '../../common/Loader';
import MssAlert from '../../common/MssAlert';
import MssApiError from '../../common/MssApiError';
import MssDelete from '../../common/MssDelete';
import { ResourceRow } from './ResourceRow';
import { useResources } from '../helpers/useResources';

/**
 * The phone view of one kind of resource: a scrolling list of rows, each of
 * which can reveal its own Edit and Delete buttons.
 *
 * Props:
 *   id           (string) - ID prefix for generated element IDs.
 *   resourceType (object) - One entry from RESOURCE_TYPES.
 *   searchTerm   (string) - Filters the list by title.
 */
export const ResourceList = ({ id, resourceType, searchTerm }) => {
  const navigate = useNavigate();
  const [openRowId, setOpenRowId] = useState(null);
  const {
    resources,
    isLoading,
    loadError,
    reload,
    isDeleting,
    resourceToDelete,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useResources(resourceType);

  const { label, lowercasePlural, route, hasEditor } = resourceType;

  const visibleResources = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return resources;
    return resources.filter((resource) => resource.title?.toLowerCase().includes(term));
  }, [resources, searchTerm]);

  if (loadError) {
    return (
      <MssAlert id={`${id}__loadError`} severity="error" type="statusAlert">
        <MssApiError id={`${id}__resources`} widgetName={lowercasePlural} reload={reload} />
      </MssAlert>
    );
  }

  return (
    <Box className="resourceList" id={`${id}__list`}>
      <Loader id={`${id}__delete`} isOpen={isDeleting} />
      {resourceToDelete && (
        <MssDelete
          id={`${id}__confirmDelete`}
          onToggle={cancelDelete}
          onDelete={confirmDelete}
          title={`Delete ${label}`}
          resourceName={resourceToDelete.title}
          isLoading={isDeleting}
          maxWidth="sm"
        />
      )}

      {isLoading && (
        <Box className="resourceListLoading">
          {[1, 2, 3].map((i) => (
            <Box key={i} className="resourceRow">
              <Box className="resourceRowMain">
                <Box className="resourceRowText">
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="30%" />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!isLoading && visibleResources.length === 0 && (
        <Typography className="resourceListEmpty">
          {searchTerm.trim()
            ? `No ${lowercasePlural} match your search.`
            : `No ${lowercasePlural} found.`}
        </Typography>
      )}

      {visibleResources.map((resource) => (
        <ResourceRow
          key={resource.id}
          id={id}
          resource={resource}
          hasEditor={hasEditor}
          areActionsOpen={openRowId === resource.id}
          onToggleActions={() =>
            setOpenRowId((current) => (current === resource.id ? null : resource.id))
          }
          onOpen={() => navigate(`${route}/${resource.id}`, { state: { title: resource.title } })}
          onEdit={() => navigate(`${route}/${resource.id}/edit`)}
          onDelete={() => requestDelete(resource)}
        />
      ))}
    </Box>
  );
};
